"use client";

import { useMemo, useCallback } from "react";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { addHibachiAccount } from "@/utils/airdrop-farming/hibachi-auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useAccount } from "wagmi";

/**
 * Session status for Hibachi connection flow.
 * Compatible with ConnectAccountStep expectations.
 */
export type HibachiSessionStatus =
  | "checking-wallet"
  | "needs-wallet"
  | "needs-approval"
  | "checking-activation"
  | "ready";

/**
 * Hook for managing Hibachi connection flow via API key input.
 *
 * Flow:
 * 1. User creates API credentials on hibachi.xyz
 * 2. User enters apiKey, privateKey, and accountId in the form
 * 3. Credentials are sent to backend which stores them securely
 */
export function useHibachiConnection() {
  const { auth, hubUser, walletStatus } = useHubUser();
  const apiHelper = useApi();
  const { address: connectedAddress, isConnected: isWalletConnected } = useAccount();
  const { authenticated } = useAuthenticationProvider();
  const queryClient = useQueryClient();

  const hibachiAccounts = useMemo(
    () => hubUser?.exchangeAccounts?.filter((a) => a.exchangeId === SUPPORTED_EXCHANGES.HIBACHI) ?? [],
    [hubUser?.exchangeAccounts],
  );

  const hasHibachiAccount = hibachiAccounts.length > 0;

  const {
    mutateAsync: connectHibachi,
    isPending: isConnecting,
    error: connectionError,
    reset: resetConnectionError,
  } = useMutation({
    mutationFn: async (params: { apiKey: string; privateKey: string; accountId: string }) => {
      await addHibachiAccount(apiHelper, auth, {
        apiKey: params.apiKey,
        privateKey: params.privateKey,
        accountId: params.accountId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      enqueueSnackbar("Hibachi account connected successfully", { variant: "success" });
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : "Failed to connect Hibachi";
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const approveAgent = useCallback(
    (params?: { apiKey?: string; privateKey?: string; accountId?: string }) => {
      if (!params?.apiKey || !params?.privateKey || !params?.accountId) {
        return Promise.reject(new Error("API credentials required"));
      }
      return connectHibachi({
        apiKey: params.apiKey,
        privateKey: params.privateKey,
        accountId: params.accountId,
      });
    },
    [connectHibachi],
  );

  const sessionStatus: HibachiSessionStatus =
    walletStatus === "initializing" || walletStatus === "connecting"
      ? "checking-wallet"
      : !authenticated
        ? "needs-wallet"
        : isConnecting
          ? "checking-activation"
          : hasHibachiAccount
            ? "ready"
            : "needs-approval";

  return {
    // Wallet state
    connectedAddress,
    isWalletConnected: isWalletConnected && authenticated,

    // Account state
    hibachiAccounts,
    hasHibachiAccount,

    // Actions
    connectHibachi,
    isConnecting,
    connectionError,
    resetConnectionError,

    // Session status (compatible with ConnectAccountStep)
    sessionStatus,

    // ── Shape compatible with useHyperliquidConnection for useLaunchBotForm ──
    agentData: {
      accountId: hibachiAccounts[0]?.id ?? undefined,
      agentAddress: hibachiAccounts[0]?.publicApiKey ?? "",
      agentName: "",
    },
    hasAgentData: hasHibachiAccount,
    isLoadingAgent: false,
    isCheckingEligibility: false,
    isNotEligibleError: false,
    eligibilityData: { eligible: true } as const,
    isEligible: true,
    // Map Hibachi connect → HL approve shape
    approveAgent,
    isApproving: isConnecting,
    approvalError: connectionError,
    resetApprovalError: resetConnectionError,
    // Not applicable for Hibachi
    updateHyperliquidAgentStatus: async () => {},
    isUpdatingAgentStatus: false,
    isLoading: isConnecting,
    walletStatus,

    // Authenticated check
    authenticated,
  };
}
